import React, { useEffect, useState } from "react";
import styled from "styled-components";

/* ===== API base (your helper) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* ================== STYLES ================== */
const FormContainer = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #3dccc7 0%, #2a5a7a 100%);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) { padding: 3rem 1.5rem; }
  @media (max-width: 480px) { padding: 2rem 1rem; }
`;

const FormContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const FormTitle = styled.h2`
  text-align: center;
  color: white;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) { font-size: 2rem; }
  @media (max-width: 480px) { font-size: 1.8rem; }
`;

const FormSubtitle = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) { font-size: 1.1rem; margin-bottom: 1rem; }
  @media (max-width: 480px) { font-size: 1rem; margin-bottom: 0.5rem; }
`;

const FormCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) { padding: 1.5rem; border-radius: 15px; }
  @media (max-width: 480px) { padding: 0.5rem; border-radius: 10px; }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (max-width: 480px) { gap: 0.6rem; }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #2c3e50;
  font-size: 1rem;
`;

const Input = styled.input`
  padding: 1rem;
  border: 2px solid #e1e8ed;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3dccc7;
    box-shadow: 0 0 0 3px rgba(61, 114, 148, 0.1);
  }

  &::placeholder { color: #a0aec0; }

  @media (max-width: 480px) { padding: 0.8rem; font-size: 0.9rem; }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 10px;
  border: 2px solid #e1e8ed;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: #3dccc7;
`;

const CheckboxLabel = styled.label`
  font-weight: 500;
  color: #2c3e50;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: white;
  padding: 1.2rem 2rem;
  border: none;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 145, 77, 0.3);

  &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(255, 145, 77, 0.4); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  @media (max-width: 768px) { padding: 1rem 1.5rem; font-size: 1rem; }
  @media (max-width: 480px) { padding: 0.8rem 1.2rem; font-size: 0.9rem; }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid #c3e6cb;
  font-weight: 500;
  flex: 1;
`;

const ErrorBanner = styled.div`
  background: #fdecea;
  color: #611a15;
  border: 1px solid #f5c6cb;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
`;

/* ===== Animated bubbles ===== */
const Bubbles = styled.ul`
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
`;

const Bubble = styled.li`
  position: absolute;
  top: var(--top, 50%);
  left: var(--left, 50%);
  width: var(--size, 24px);
  height: var(--size, 24px);
  border-radius: 50%;
  opacity: 0.35;
  background:
    radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(255,255,255,0.4) 60%, rgba(255,255,255,0) 70%);
  box-shadow:
    inset 0 -2px 6px rgba(255,255,255,0.45),
    0 6px 14px rgba(0,0,0,0.12);
  filter: blur(0.2px);
  animation:
    swayX var(--sway, 6s) ease-in-out infinite alternate,
    bobY  var(--bob, 7s)  ease-in-out infinite alternate;
  animation-delay: var(--delay, 0s), var(--delay, 0s);

  @keyframes swayX {
    0%   { margin-left: calc(var(--drift, 40px) * -1); }
    100% { margin-left: var(--drift, 40px); }
  }
  @keyframes bobY {
    0%   { margin-top: calc(var(--bobDist, 12px) * -1); }
    100% { margin-top: var(--bobDist, 12px); }
  }
`;

/* ===== Modal ===== */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: grid;
  place-items: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalCard = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 560px;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.25);
  position: relative;
  padding: 1rem;
`;

const ModalRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: #6b7280;
  border-radius: 8px;
  padding: 0.25rem;

  &:hover { background: #f3f4f6; color: #374151; }
  &:focus-visible { outline: 2px solid #3dccc7; outline-offset: 2px; }
`;

/* ================== COMPONENT ================== */
const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  webinarSignup: false,
};

const EBookForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedWebinar, setSubmittedWebinar] = useState(false); // preserve flag after reset
  const [serverMsg, setServerMsg] = useState(""); // message from backend (new vs already_subscribed)
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ====== SUBMIT -> CALL BACKEND ====== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    setServerMsg("");

    // Optional guard
    if (!API_BASE) {
      setErrMsg(
        "API base URL is not configured. Set VITE_API_BASE_URL (or REACT_APP_API_BASE_URL)."
      );
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/email-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 422 && Array.isArray(payload?.errors) && payload.errors.length) {
          // show first validator error
          throw new Error(payload.errors[0].msg);
        }
        throw new Error(payload?.message || "Failed to submit. Try again.");
      }

      if (payload?.ok === false) {
        throw new Error(payload?.message || "Failed to submit. Try again.");
      }

      // success:
      setServerMsg(
        payload?.message ||
          (payload?.status === "already_subscribed"
            ? "You’re already on the list. We’ve updated your preferences."
            : "Thanks! You’re subscribed. The ebook will arrive shortly.")
      );

      // preserve the checkbox state for the modal text
      setSubmittedWebinar(!!formData.webinarSignup);

      setIsSubmitted(true);
      setFormData(initialFormData);
    } catch (err) {
      setErrMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsSubmitted(false);
    setServerMsg("");
    setSubmittedWebinar(false);
  };

  useEffect(() => {
    if (!isSubmitted) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isSubmitted]);

  const bubbleVars = [
    { size: "34px", duration: "8s",  delay: "0s",   left: "6%",  top: "16%", sway: "4.5s", bob: "5.5s", drift: "48px", bobDist: "20px" },
    { size: "42px", duration: "9s",  delay: "0.7s", left: "13%", top: "62%", sway: "5s",   bob: "5.8s", drift: "56px", bobDist: "22px" },
    { size: "48px", duration: "9.5s",delay: "0.3s", left: "21%", top: "38%", sway: "5.2s", bob: "6s",   drift: "64px", bobDist: "24px" },
    { size: "30px", duration: "7.5s",delay: "1.4s", left: "28%", top: "28%", sway: "4.2s", bob: "5s",   drift: "44px", bobDist: "18px" },
    { size: "54px", duration: "10s", delay: "0.2s", left: "36%", top: "74%", sway: "5.6s", bob: "6.2s", drift: "70px", bobDist: "26px" },
    { size: "38px", duration: "8.5s",delay: "1.8s", left: "44%", top: "20%", sway: "4.8s", bob: "5.4s", drift: "52px", bobDist: "20px" },
    { size: "46px", duration: "9.2s",delay: "1.1s", left: "52%", top: "54%", sway: "5.2s", bob: "5.9s", drift: "62px", bobDist: "22px" },
    { size: "58px", duration: "10.5s",delay: "0.5s",left: "60%", top: "34%", sway: "5.8s", bob: "6.3s", drift: "76px", bobDist: "28px" },
    { size: "34px", duration: "8s",  delay: "1.9s", left: "68%", top: "58%", sway: "4.6s", bob: "5.2s", drift: "50px", bobDist: "20px" },
    { size: "64px", duration: "11s", delay: "0.4s", left: "76%", top: "26%", sway: "6s",   bob: "6.6s", drift: "84px", bobDist: "30px" },
    { size: "40px", duration: "9s",  delay: "2.3s", left: "84%", top: "78%", sway: "5.2s", bob: "6s",   drift: "58px", bobDist: "22px" },
    { size: "30px", duration: "7.5s",delay: "2.8s", left: "92%", top: "12%", sway: "4.1s", bob: "4.9s", drift: "42px", bobDist: "18px" },
    { size: "28px", duration: "7.8s",delay: "1.2s", left: "10%", top: "48%", sway: "4.3s", bob: "5.1s", drift: "40px", bobDist: "18px" },
    { size: "36px", duration: "8.7s",delay: "0.9s", left: "19%", top: "22%", sway: "4.9s", bob: "5.6s", drift: "52px", bobDist: "20px" },
    { size: "44px", duration: "9.6s",delay: "1.6s", left: "33%", top: "66%", sway: "5.3s", bob: "6.1s", drift: "60px", bobDist: "24px" },
    { size: "50px", duration: "10.2s",delay: "0.6s",left: "41%", top: "42%", sway: "5.7s", bob: "6.4s", drift: "72px", bobDist: "26px" },
    { size: "28px", duration: "7.9s",delay: "2.1s", left: "49%", top: "14%", sway: "4.4s", bob: "5.2s", drift: "40px", bobDist: "18px" },
    { size: "34px", duration: "8.4s",delay: "1.3s", left: "57%", top: "70%", sway: "4.8s", bob: "5.6s", drift: "50px", bobDist: "20px" },
    { size: "42px", duration: "9.1s",delay: "2.5s", left: "65%", top: "44%", sway: "5.1s", bob: "5.9s", drift: "58px", bobDist: "22px" },
    { size: "56px", duration: "10.8s",delay: "0.7s",left: "73%", top: "18%", sway: "5.9s", bob: "6.5s", drift: "74px", bobDist: "28px" },
    { size: "32px", duration: "8.2s",delay: "2.9s", left: "81%", top: "52%", sway: "4.7s", bob: "5.4s", drift: "48px", bobDist: "20px" },
    { size: "48px", duration: "9.8s",delay: "1.7s", left: "89%", top: "32%", sway: "5.5s", bob: "6.2s", drift: "66px", bobDist: "24px" },
    { size: "60px", duration: "11.2s",delay: "0.3s",left: "50%", top: "86%", sway: "6.1s", bob: "6.7s", drift: "80px", bobDist: "30px" },
  ];

  return (
    <>
      <FormContainer>
        <Bubbles aria-hidden="true">
          {bubbleVars.map((b, i) => (
            <Bubble
              key={i}
              style={{
                "--size": b.size,
                "--delay": b.delay,
                "--left": b.left,
                "--top": b.top,
                "--sway": b.sway,
                "--bob": b.bob,
                "--drift": b.drift,
                "--bobDist": b.bobDist,
              }}
            />
          ))}
        </Bubbles>

        <FormContent>
          <FormTitle>Get Your Free Guide on How to use ChatGPT in Construction</FormTitle>
          <FormSubtitle>
            Download our comprehensive eBook and learn how to revolutionize your
            construction projects with AI
          </FormSubtitle>

          <FormCard>
            {errMsg && <ErrorBanner>{errMsg}</ErrorBanner>}

            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                  disabled={loading}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                  disabled={loading}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </InputGroup>

              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="webinarSignup"
                  name="webinarSignup"
                  checked={formData.webinarSignup}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <CheckboxLabel htmlFor="webinarSignup">
                  I want to register for upcoming AI construction webinars
                </CheckboxLabel>
              </CheckboxGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? "Submitting..." : "Download Free eBook"}
              </SubmitButton>
            </Form>
          </FormCard>
        </FormContent>
      </FormContainer>

      {isSubmitted && (
        <ModalOverlay
          role="dialog"
          aria-modal="true"
          aria-labelledby="success-title"
          onClick={closeModal}
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalRow>
              <SuccessMessage id="success-title">
                {serverMsg || "🎉 Thank you! Your free eBook will be sent to your email shortly."}
                {submittedWebinar && " You’re also registered for our upcoming webinars!"}
              </SuccessMessage>
              <CloseButton aria-label="Close" onClick={closeModal}>✕</CloseButton>
            </ModalRow>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default EBookForm;
